import React from 'react';
import { motion } from 'framer-motion';
import { StyleSheet, css } from 'aphrodite';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  dateOfBirth: Yup.date()
    .required('Date of birth is required')
    .max(new Date(), 'Date of birth cannot be in the future')
    .test(
      'is-valid-age',
      'You must be at least 1 day old',
      (value) =>
        value && new Date().getTime() - value.getTime() >= 24 * 60 * 60 * 1000
    ),
  gender: Yup.string().required('Gender is required')
});

const optionalValidationSchema = Yup.object().shape({
  firstName: Yup.string(),
  lastName: Yup.string(),
  dateOfBirth: Yup.date()
    .max(new Date(), 'Date of birth cannot be in the future')
    .test(
      'is-valid-age',
      'You must be at least 1 day old',
      (value) =>
        value && new Date().getTime() - value.getTime() >= 24 * 60 * 60 * 1000
    ),
  gender: Yup.string()
});

const PersonalInformation = ({ initialData, onComplete, onBack, optional }) => {
  return (
    <motion.div
      className={css(styles.container)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className={css(styles.title)}>Personal Information</h2>
      <Formik
        initialValues={{
				  firstName: initialData.firstName || '',
				  lastName: initialData.lastName || '',
				  dateOfBirth: initialData.dateOfBirth || '',
				  gender: initialData.gender || ''
        }}
        validationSchema={
					!optional ? validationSchema : optionalValidationSchema
				}
				// onSubmit={(values, { setSubmitting }) => {
				// 	onComplete(values, true);
				// 	setSubmitting(false);
				// }}
        onSubmit={(values) => {
				  // Check if all required fields are filled
				  const isValid = Object.keys(values).every((key) =>
				    Boolean(values[key])
          );
				  onComplete(values, isValid);
				  setSubmitting(false);
        }}
      >
        {({ errors, touched, isValid, dirty }) => (
          <Form className={css(styles.form)}>
            <div className={css(styles.fieldGroup)}>
              <label htmlFor='firstName' className={css(styles.label)}>
                First Name
              </label>
              <Field name='firstName' className={css(styles.input)} />
              {errors.firstName && touched.firstName && (
                <div className={css(styles.error)}>{errors.firstName}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='lastName' className={css(styles.label)}>
                Last Name
              </label>
              <Field name='lastName' className={css(styles.input)} />
              {errors.lastName && touched.lastName && (
                <div className={css(styles.error)}>{errors.lastName}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='dateOfBirth' className={css(styles.label)}>
                Date of Birth
              </label>
              <Field
                name='dateOfBirth'
                type='date'
                className={css(styles.input)}
                max={new Date().toISOString().split('T')[0]}
              />
              {errors.dateOfBirth && touched.dateOfBirth && (
                <div className={css(styles.error)}>{errors.dateOfBirth}</div>
              )}
            </div>

            <div className={css(styles.fieldGroup)}>
              <label htmlFor='gender' className={css(styles.label)}>
                Gender
              </label>
              <Field name='gender' as='select' className={css(styles.input)}>
                <option value=''>Select Gender</option>
                <option value='male'>Male</option>
                <option value='female'>Female</option>
                <option value='other'>Other</option>
              </Field>
              {errors.gender && touched.gender && (
                <div className={css(styles.error)}>{errors.gender}</div>
              )}
            </div>

            <div className={css(styles.buttonContainer)}>
              <motion.button
                type='submit'
                className={css(
								  styles.button,
								  styles.nextButton,
								  !(isValid && dirty) && styles.disabledButton
                )}
                whileHover={isValid && dirty ? { scale: 1.05 } : {}}
                whileTap={isValid && dirty ? { scale: 0.95 } : {}}
                disabled={!(isValid && dirty)}
              >
                {!optional ? 'Next' : 'Update Profile'}
              </motion.button>
              {!optional
                ? (
                  <motion.button
                    type='button'
                    className={css(styles.button, styles.backButton)}
                    onClick={onBack}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Back
                  </motion.button>
                  )
                : null}
            </div>
          </Form>
        )}
      </Formik>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: '2rem'
  },
  title: {
    fontSize: '1.5rem',
    marginBottom: '1rem',
    color: '#2783F7'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    marginBottom: '0.5rem',
    fontSize: '1rem',
    color: '#4a5568'
  },
  input: {
    padding: '0.5rem',
    fontSize: '1rem',
    borderRadius: '5px',
    border: '1px solid #e2e8f0'
  },
  error: {
    color: 'red',
    fontSize: '0.875rem',
    marginTop: '0.25rem'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem'
  },
  button: {
    border: 'none',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  nextButton: {
    backgroundColor: '#2783F7',
    color: 'white'
  },
  backButton: {
    backgroundColor: '#e2e8f0',
    color: '#4a5568'
  },
  disabledButton: {
    backgroundColor: '#a0aec0',
    cursor: 'not-allowed'
  }
});

export default PersonalInformation;
